/* Functions to handle units for the HDS Monitor now that we want to set different units for different clients.
   To do: - Add precision to all the unit types
          - Add custom organization support (Suncor ICP weld map, TM KP Mapping, Enbridge Line 3R4).
*/

import { gqlType } from "@hifieng/common";
// import { mapTmKps } from "./tmKpMapping";
// import { useAuth } /from "../contexts/AuthProvider";
import { IPermissions } from "../types/AuthTypes";

const defaultPrecision = 2;

export const kpMpRatio = 0.621371;
const mpPrecision = 3;
export const kpToMp = (
  kp: number,
  decimals = mpPrecision,
  format: "prefix" | "suffix" | "none"
) => {
  const newDecimals = decimals === defaultPrecision ? mpPrecision : decimals;
  const result = (kpMpRatio * kp).toFixed(newDecimals);

  if (format === "prefix") {
    return `MP ${result}`;
  } else if (format === "suffix") {
    return `${result} miles`;
  } else {
    return parseFloat(result);
  }
};

// eslint-disable-next-line no-magic-numbers
export const kpSpRatio = 3280.84 / 100;
export const kpToSp = (kp: number, format: "prefix" | "suffix" | "none") => {
  const result = (kpSpRatio * kp).toFixed(0);

  if (format === "prefix") {
    return `SP ${result}`;
  } else if (format === "suffix") {
    return `${result} SP`;
  } else {
    return parseFloat(result);
  }
};

// eslint-disable-next-line no-magic-numbers
export const kpFtRatio = 3280.84;
export const kpToFt = (kp: number, format: "prefix" | "suffix" | "none") => {
  const result = (kpFtRatio * kp).toFixed(0);

  if (format === "prefix" || format === "suffix") {
    return `${result} ft`;
  } else {
    return parseFloat(result);
  }
};

export const kpToKp = (kp: number, format: "prefix" | "suffix" | "none") => {
  if (format === "prefix") {
    return `KP ${kp}`;
  } else if (format === "suffix") {
    return `${kp} km`;
  } else {
    return kp;
  }
};

export const getOrgUnits = (activeOrg: gqlType.Organization) => {
  const outputObject: { [key: string]: string } = {};
  activeOrg.pipelines.forEach(pipeline => {
    outputObject[pipeline.id] = pipeline.interfaceOptions.unit
      ? pipeline.interfaceOptions.unit
      : "metric";
  });
  return outputObject;
};

// const { permissions } = useAuth();
// const imperialFlag =
//   (permissions.user.imperial ? true : false) ||
//   fiber.id === "enbridge_line_3" ||
//   fiber.id === "enbridge_line_4";
// const spFlag = permissions.user.station ? true : false;
// const ftFlag = permissions.user.ft ? true : false;

// const imperialFlag = permissions.user.imperial ? true : false;

export const getUnit = (
  format: "heading" | "unit" = "unit",
  activeOrg?: gqlType.Organization | undefined,
  pipelineId?: string | undefined,
  permissions?: IPermissions | undefined,
) => {
  // Add pipeline/org specific unit settings here...
  // examples to implement in the future: Suncor ICP weld map, TM KP Mapping, Enbridge Line3R4
  console.log("format", format);
  console.log("activeOrg", activeOrg);
  console.log("pipelineId", pipelineId);
  console.log("permissions", permissions);
  // Auth0 permissions take preference over organization settings
  if (permissions) {
    if (permissions.user.imperial) {
      return format === "heading" ? `Mile Post` : `MP`;
    } else if (permissions.user.station) {
      return format === "heading" ? `Station Point` : `SP`;
    } else if (permissions.user.ft) {
      return format === "heading" ? `Feet` : `ft`;
    }
  }

  // If no org then use metric
  if (!activeOrg) return format === "heading" ? `KM Post` : `KP`;

  // if no auth0 permissions and org exists then check for org unit settings.
  if (pipelineId) {
    const orgUnits = getOrgUnits(activeOrg);
    const unit = orgUnits[pipelineId] || "metric";

    if (unit === "imperial") {
      return format === "heading" ? `Mile Post` : `MP`;
    } else if (unit === "station") {
      return format === "heading" ? `Station Point` : `SP`;
    } else if (unit === "ft") {
      return format === "heading" ? `Feet` : `ft`;
    } else {
      return format === "heading" ? `KM Post` : `KP`;
    }
  }

  // If none resolved return metric
  return format === "heading" ? `KM Post` : `KP`;
};

export const getUnitOutput = (
  kp: number,
  format: "prefix" | "suffix" | "none" = "none",
  activeOrg?: gqlType.Organization | undefined,
  pipelineId?: string | undefined,
  permissions?: IPermissions | undefined,
  decimals = defaultPrecision
) => {
  // Add pipeline/org specific unit settings here...
  // examples to implement in the future: Suncor ICP weld map, TM KP Mapping, Enbridge Line3R4
  console.log("format", format);
  console.log("activeOrg", activeOrg);
  console.log("pipelineId", pipelineId);
  console.log("permissions", permissions);
  // Auth0 permissions take preference over organization settings
  if (permissions) {
    if (permissions.user.imperial) {
      return kpToMp(kp, decimals, format);
    } else if (permissions.user.station) {
      return kpToSp(kp, format);
    } else if (permissions.user.ft) {
      return kpToFt(kp, format);
    }
  }

  // If no org then use metric
  if (!activeOrg) return kpToKp(kp, format);

  // if no auth0 permissions and org exists then check for org unit settings.
  if (pipelineId) {
    const orgUnits = getOrgUnits(activeOrg);
    const unit = orgUnits[pipelineId] || "metric";

    if (unit === "imperial") {
      return kpToMp(kp, decimals, format);
    } else if (unit === "station") {
      return kpToSp(kp, format);
    } else if (unit === "ft") {
      return kpToFt(kp, format);
    } else {
      return kpToKp(kp, format);
    }
  }

  // If none resolved return metric
  return kpToKp(kp, format);
};

export const getUnitConvertFun = (
  activeOrg?: gqlType.Organization | undefined,
  pipelineId?: string | undefined,
  permissions?: IPermissions | undefined,
) => {
  // Add pipeline/org specific unit settings here...
  // examples to implement in the future: Suncor ICP weld map, TM KP Mapping, Enbridge Line3R4
  console.log("activeOrg", activeOrg);
  console.log("pipelineId", pipelineId);
  console.log("permissions", permissions);
  // Auth0 permissions take preference over organization settings
  if (permissions) {
    if (permissions.user.imperial) {
      return kpToMp;
    } else if (permissions.user.station) {
      return kpToSp;
    } else if (permissions.user.ft) {
      return kpToFt;
    }
  }

  // If no org then use metric
  if (!activeOrg) return kpToKp;

  // if no auth0 permissions and org exists then check for org unit settings.
  if (pipelineId) {
    const orgUnits = getOrgUnits(activeOrg);
    const unit = orgUnits[pipelineId] || "metric";

    if (unit === "imperial") {
      return kpToMp;
    } else if (unit === "station") {
      return kpToSp;
    } else if (unit === "ft") {
      return kpToFt;
    } else {
      return kpToKp;
    }
  }

  // If none resolved return metric
  return kpToKp;
};

export const getUnitFlag = (
  activeOrg?: gqlType.Organization | undefined,
  pipelineId?: string | undefined,
  permissions?: IPermissions | undefined,
) => {
  // Add pipeline/org specific unit settings here...
  // examples to implement in the future: Suncor ICP weld map, TM KP Mapping, Enbridge Line3R4
  console.log("activeOrg", activeOrg);
  console.log("pipelineId", pipelineId);
  console.log("permissions", permissions);
  // Auth0 permissions take preference over organization settings
  if (permissions) {
    if (permissions.user.imperial) {
      return "imperial";
    } else if (permissions.user.station) {
      return "station";
    } else if (permissions.user.ft) {
      return "ft";
    }
  }

  // If no org then use metric
  if (!activeOrg) return "metric";

  // if no auth0 permissions and org exists then check for org unit settings.
  if (pipelineId) {
    const orgUnits = getOrgUnits(activeOrg);
    const unit = orgUnits[pipelineId] || "metric";

    return unit
  }

  // If none resolved return metric
  return "metric";
};
